'use client'

import { NavigationMenu as BaseNavigationMenu } from '@base-ui/react/navigation-menu'
import * as stylex from '@stylexjs/stylex'
import { cloneElement, type ReactElement, type ReactNode } from 'react'

import {
  layers,
  colors,
  fonts,
  fontSizes,
  radii,
  spacing,
  elevation,
  breakpoints,
  controlSizes,
  motion,
} from '../tokens/tokens.stylex'

const styles = stylex.create({
  list: {
    alignItems: 'center',
    display: 'flex',
    gap: spacing.space20,
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  trigger: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderWidth: 0,
    color: { default: colors.secondary, ':hover': colors.foreground },
    cursor: 'pointer',
    display: 'flex',
    fontFamily: fonts.interface,
    fontSize: fontSizes.label,
    gap: spacing.space4,
    letterSpacing: '0.01em',
    lineHeight: '18px',
    minHeight: { default: controlSizes.navigation, [breakpoints.coarsePrimary]: controlSizes.touch },
    outline: { default: 'none', ':focus-visible': `2px solid ${colors.foreground}` },
    outlineOffset: 4,
    paddingBlock: spacing.space8,
    paddingInline: 0,
  },
  plainLink: { textDecoration: 'none' },
  triggerOpen: { color: colors.foreground },
  icon: {
    display: 'flex',
    transitionDuration: { default: motion.navigationDuration, [breakpoints.reducedMotion]: '0ms' },
    transitionProperty: 'transform',
  },
  iconOpen: { transform: 'rotate(180deg)' },
  positioner: {
    height: 'var(--positioner-height)',
    transitionDuration: { default: motion.navigationDuration, [breakpoints.reducedMotion]: '0ms' },
    transitionProperty: 'left, top, transform',
    transitionTimingFunction: motion.navigationEasing,
    width: 'var(--positioner-width)',
    zIndex: layers.navigation,
  },
  instant: { transitionDuration: '0ms' },
  popup: {
    backgroundColor: colors.background,
    borderColor: colors.border,
    borderRadius: radii.dialog,
    borderStyle: 'solid',
    borderWidth: 1,
    boxShadow: elevation.navigation,
    boxSizing: 'border-box',
    color: colors.foreground,
    height: 'var(--popup-height)',
    maxHeight: 'var(--available-height)',
    maxWidth: 'var(--available-width)',
    opacity: 1,
    outline: 'none',
    overflow: 'hidden',
    position: 'relative',
    transitionDuration: { default: motion.navigationDuration, [breakpoints.reducedMotion]: '0ms' },
    transitionProperty: 'width, height, opacity',
    transitionTimingFunction: motion.navigationEasing,
    width: 'var(--popup-width)',
  },
  hidden: { opacity: 0 },
  viewport: { height: '100%', position: 'relative', width: '100%' },
  content: {
    boxSizing: 'border-box',
    maxHeight: 'var(--available-height)',
    maxWidth: 'var(--available-width)',
    minWidth: 228,
    overflowY: 'auto',
    padding: spacing.space8,
    width: 'max-content',
  },
  links: { listStyle: 'none', margin: 0, padding: 0 },
  link: {
    alignItems: 'baseline',
    backgroundColor: { default: 'transparent', ':hover': colors.hover },
    borderRadius: radii.small,
    color: colors.foreground,
    display: 'flex',
    fontFamily: fonts.interface,
    fontSize: fontSizes.label,
    gap: spacing.space8,
    letterSpacing: '0.01em',
    lineHeight: '18px',
    outline: { default: 'none', ':focus-visible': `2px solid ${colors.foreground}` },
    outlineOffset: -2,
    paddingBlock: spacing.space8,
    paddingInline: spacing.space12,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  activeLink: { backgroundColor: colors.selectedSurface, color: colors.selected },
  note: { color: colors.muted, fontFamily: fonts.sans, fontSize: fontSizes.small, letterSpacing: 'normal' },
  external: { fontFamily: fonts.sans, fontSize: fontSizes.small },
})

export type NavigationMenuItem = {
  label: string
  href: string
  note?: string
  external?: boolean
  active?: boolean
}

export type NavigationMenuGroup = {
  label: string
  /** Makes the label itself a link. With no items the group is a plain top-level link with no panel. */
  href?: string
  items: readonly NavigationMenuItem[]
  /** Keeps the trigger and static items mounted while application-owned content resolves. */
  renderAdditionalItems?: (renderItem: (item: NavigationMenuItem) => ReactNode) => ReactNode
}

export type NavigationMenuProps = {
  items: readonly NavigationMenuGroup[]
  renderLink?: (item: NavigationMenuItem) => ReactElement
  ariaLabel?: string
}

/**
 * Use for grouped desktop destinations; choose Menu for actions and NavigationDrawer for mobile navigation.
 * Applications supply destinations, active states, and router link adapters. Set the theme on the document root
 * so portaled panels inherit it. Status: supported.
 */
export function NavigationMenu({ items, renderLink, ariaLabel = 'Main navigation' }: NavigationMenuProps) {
  const renderItem = (item: NavigationMenuItem) => {
    const content = (
      <>
        {item.label}
        {item.note && <span {...stylex.props(styles.note)}>{item.note}</span>}
        {item.external && (
          <span aria-hidden="true" {...stylex.props(styles.external)}>
            ↗
          </span>
        )}
      </>
    )

    const link = renderLink?.(item)

    return (
      <li key={item.href}>
        <BaseNavigationMenu.Link
          href={item.href}
          render={link && cloneElement(link, undefined, content)}
          active={item.active}
          closeOnClick
          target={item.external ? '_blank' : undefined}
          rel={item.external ? 'noopener noreferrer' : undefined}
          {...stylex.props(styles.link, item.active && styles.activeLink)}
        >
          {content}
        </BaseNavigationMenu.Link>
      </li>
    )
  }

  return (
    <BaseNavigationMenu.Root aria-label={ariaLabel}>
      <BaseNavigationMenu.List {...stylex.props(styles.list)}>
        {items.map((group) =>
          group.href && group.items.length === 0 && !group.renderAdditionalItems ? (
            <li key={group.label}>
              <BaseNavigationMenu.Link
                href={group.href}
                render={renderLink?.({ label: group.label, href: group.href })}
                {...stylex.props(styles.trigger, styles.plainLink)}
              >
                {group.label}
              </BaseNavigationMenu.Link>
            </li>
          ) : (
            <BaseNavigationMenu.Item key={group.label} value={group.label}>
              <BaseNavigationMenu.Trigger
                {...(group.href
                  ? {
                      nativeButton: false,
                      render: renderLink?.({ label: group.label, href: group.href }) ?? (
                        <a href={group.href}>{group.label}</a>
                      ),
                    }
                  : {})}
                className={(state) => stylex.props(styles.trigger, state.open && styles.triggerOpen).className}
              >
                {group.label}
                <BaseNavigationMenu.Icon
                  className={(state) => stylex.props(styles.icon, state.open && styles.iconOpen).className}
                >
                  <svg width="9" height="5" viewBox="0 0 9 5" aria-hidden="true">
                    <path d="m1 .5 3.5 3.5L8 .5" fill="none" stroke="currentColor" strokeLinecap="round" />
                  </svg>
                </BaseNavigationMenu.Icon>
              </BaseNavigationMenu.Trigger>
              <BaseNavigationMenu.Content {...stylex.props(styles.content)}>
                <ul {...stylex.props(styles.links)}>
                  {group.items.map(renderItem)}
                  {group.renderAdditionalItems?.(renderItem)}
                </ul>
              </BaseNavigationMenu.Content>
            </BaseNavigationMenu.Item>
          )
        )}
      </BaseNavigationMenu.List>
      <BaseNavigationMenu.Portal>
        <BaseNavigationMenu.Positioner
          align="start"
          sideOffset={10}
          collisionPadding={16}
          className={(state) => stylex.props(styles.positioner, state.instant && styles.instant).className}
        >
          <BaseNavigationMenu.Popup
            aria-label={ariaLabel}
            className={(state) => stylex.props(styles.popup, state.transitionStatus && styles.hidden).className}
          >
            <BaseNavigationMenu.Viewport {...stylex.props(styles.viewport)} />
          </BaseNavigationMenu.Popup>
        </BaseNavigationMenu.Positioner>
      </BaseNavigationMenu.Portal>
    </BaseNavigationMenu.Root>
  )
}
